import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ExternalLink, LogOut, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

type Assignment = {
  id: string;
  asin: string;
  productTitle: string | null;
  status: "pending" | "claimed" | "submitted" | "qa_passed" | "rejected";
  claimedAt: string | null;
  rejectionReason: string | null;
  amazonUrl: string;
};

type Me = {
  manualScraper: {
    id: string;
    email: string;
    name: string | null;
  };
};

type ScrapePagination = {
  page: number;
  pageSize: number;
  total: number;
  hasPrevious: boolean;
  hasNext: boolean;
};

export default function ManualScraperPortal() {
  const navigate = useNavigate();
  const [meLoading, setMeLoading] = useState(true);
  const [me, setMe] = useState<Me["manualScraper"] | null>(null);
  const [available, setAvailable] = useState<Assignment[]>([]);
  const [claimed, setClaimed] = useState<Assignment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [availablePage, setAvailablePage] = useState(1);
  const [pagination, setPagination] = useState<ScrapePagination>({
    page: 1,
    pageSize: 50,
    total: 0,
    hasPrevious: false,
    hasNext: false,
  });

  async function refresh(page = availablePage) {
    const res = await api<{
      available: Assignment[];
      claimed: Assignment[];
      pagination: ScrapePagination;
    }>(`/manual-scraper/scrapes?page=${page}`);
    if (res.ok && res.data) {
      setAvailable(res.data.available);
      setClaimed(res.data.claimed);
      setPagination(res.data.pagination);
    }
  }

  useEffect(() => {
    let cancelled = false;
    api<Me>("/manual-scraper/me").then((res) => {
      if (cancelled) return;
      if (!res.ok || !res.data?.manualScraper) {
        navigate("/manual-scrape/login", { replace: true });
        return;
      }
      setMe(res.data.manualScraper);
      setMeLoading(false);
      void refresh(availablePage);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function claim(job: Assignment) {
    setError(null);
    setClaimingId(job.id);
    const res = await api<{ ok: true }>(`/manual-scraper/scrapes/${job.id}/claim`, {
      method: "POST",
    });
    setClaimingId(null);
    if (!res.ok) {
      setError(res.error ?? "Couldn't claim — someone else may have grabbed it.");
      await refresh(availablePage);
      return;
    }
    window.location.href = job.amazonUrl;
  }

  function goToAvailablePage(page: number) {
    setAvailablePage(page);
    void refresh(page);
  }

  async function signOut() {
    const res = await api<{ ok: true; logoutUrl?: string }>("/auth/logout", {
      method: "POST",
    });
    if (res.data?.logoutUrl) {
      window.location.assign(res.data.logoutUrl);
      return;
    }
    navigate("/manual-scrape/login", { replace: true });
  }

  if (meLoading || !me) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <header className="sticky top-0 z-10 flex h-14 items-center justify-between border-b border-slate-200 bg-white px-6 dark:border-slate-800 dark:bg-slate-900">
        <div className="font-semibold tracking-tight">Reputely · Manual scraper portal</div>
        <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400">
          <span>{me.name || me.email}</span>
          <button
            type="button"
            onClick={signOut}
            className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl px-6 py-8">
        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}

        <section>
          <h2 className="text-lg font-semibold tracking-tight">Your jobs</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Click <strong>Open</strong> to enter review data for a job you've claimed.
          </p>
          <div className="mt-4 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
            {claimed.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                You haven't claimed any jobs yet — pick one below.
              </div>
            )}
            {claimed.map((a) => (
              <div key={a.id} className="flex items-center gap-4 px-5 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">{a.asin}</span>
                    {a.productTitle && (
                      <span className="truncate text-sm text-slate-700 dark:text-slate-300">
                        — {a.productTitle}
                      </span>
                    )}
                    {a.status === "rejected" && (
                      <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900">
                        Rejected — please redo
                      </span>
                    )}
                  </div>
                  {a.rejectionReason && (
                    <p className="mt-1 text-xs text-red-700 dark:text-red-300">
                      QA note: {a.rejectionReason}
                    </p>
                  )}
                </div>
                <Link
                  to={`/manual-scrape/scrapes/${a.id}`}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900"
                >
                  Open →
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold tracking-tight">
                Available jobs
              </h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                Each job is one Amazon product. Click <strong>Claim</strong> to lock it
                to you, then visit Amazon and enter the reviews you find.
              </p>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              {pagination.total === 0
                ? "0 jobs"
                : `${(pagination.page - 1) * pagination.pageSize + 1}-${Math.min(
                    pagination.page * pagination.pageSize,
                    pagination.total
                  )} of ${pagination.total}`}
            </div>
          </div>
          <div className="mt-4 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
            {available.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                No jobs available right now. Check back later.
              </div>
            )}
            {available.map((a) => (
              <div key={a.id} className="flex items-center gap-4 px-5 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">{a.asin}</span>
                    {a.productTitle && (
                      <span className="truncate text-sm text-slate-700 dark:text-slate-300">
                        — {a.productTitle}
                      </span>
                    )}
                  </div>
                  <a
                    href={a.amazonUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                  >
                    Preview on Amazon <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <button
                  type="button"
                  onClick={() => claim(a)}
                  disabled={claimingId !== null}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-900 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
                >
                  {claimingId === a.id ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Claiming
                    </>
                  ) : (
                    "Claim →"
                  )}
                </button>
              </div>
            ))}
          </div>
          {(pagination.hasPrevious || pagination.hasNext) && (
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => goToAvailablePage(pagination.page - 1)}
                disabled={!pagination.hasPrevious}
                className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Previous
              </button>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                Page {pagination.page}
              </span>
              <button
                type="button"
                onClick={() => goToAvailablePage(pagination.page + 1)}
                disabled={!pagination.hasNext}
                className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Next
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
